import { DialogActions, DialogContent, DialogTitle, List, ListItem, TextField } from '@material-ui/core'
import { Autocomplete, Skeleton } from '@material-ui/lab'
import React, { Suspense, useEffect, useState } from 'react'
import OdysseyDialog from './OdysseyDialog'
import { graphql } from 'babel-plugin-relay/macro'
import { ShareContactQuery } from '../__generated__/ShareContactQuery.graphql'
import { fetchQuery, useRelayEnvironment } from 'react-relay/hooks'
import styled from 'styled-components'
import Button from './Button'
import { ShareContactMutation } from '../__generated__/ShareContactMutation.graphql'
import useNiceMutation from '../mutations/useNiceMutation'
import LoadingDots from './LoadingDots'
import { useSnackbar } from 'notistack'

const ShareContainer = styled.div`
  margin: 20px;
`

interface Props {
  open: boolean
  slug: string
  onClose: () => void
  role: string | null
}

const toCamelCaseAndPlural = (str: string) => {
  const camelCaseStr = str.charAt(0).toUpperCase() + str.slice(1)
  if (camelCaseStr.endsWith('y')) {
    return camelCaseStr.slice(0, -1) + 'ies'
  }
  return camelCaseStr + 's'
}

const ShareContact: React.FC<Props> = (props) => {
  const role = props.role || 'consignee'

  return (
    <OdysseyDialog open={props.open} onClose={props.onClose}>
      <DialogTitle>{toCamelCaseAndPlural(role)}</DialogTitle>
      <Suspense fallback={<ShareContactSkeleton {...props} />}>
        <ShareContactContent {...props} role={role} />
      </Suspense>
    </OdysseyDialog>
  )
}

const ShareContactContent: React.FC<Props> = ({ onClose, slug, role = 'consignee' }) => {
  const environment = useRelayEnvironment()
  const { enqueueSnackbar } = useSnackbar()
  const [removedSlugs, setRemovedSlugs] = useState<Set<string>>(new Set())
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [options, setOptions] = useState<any[]>([])
  const [selectUser, setSelectUser] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [commit, commitLoading] = useNiceMutation<ShareContactMutation>(mutation)

  useEffect(() => {
    const handle = setTimeout(() => setSearchQuery(inputValue), 300)
    return () => clearTimeout(handle)
  }, [inputValue])

  useEffect(() => {
    setLoading(true)
    fetchQuery<ShareContactQuery>(environment, query, {
      contactSlug: slug,
      searchQuery,
      role,
    })
      .toPromise()
      .then((data) => data?.sharedContacts ?? [])
      .then((opts) => {
        setOptions(opts as never[])
        const shared = opts.filter((u) => u.shared && !removedSlugs.has(u.slug))
        setSelectUser((prev) => {
          const merged = [...prev]
          shared.forEach((u) => {
            if (!merged.some((s) => s.slug === u.slug)) merged.push(u)
          })
          return merged
        })
      })
      .finally(() => setLoading(false))
  }, [searchQuery, environment, slug, role])

  const handleShare = () => {
    commit({
      variables: {
        input: {
          slug,
          contactSlugs: selectUser.map((u) => u.slug).join(','),
          role: role || 'consignee',
        },
      },
      onCompleted: (_, errors) => {
        if (!errors) {
          enqueueSnackbar('Contact list updated successfully', { variant: 'success' })
          onClose()
        }
      },
    })
  }

  return (
    <>
      <DialogContent>
        <ShareContainer>
          <Autocomplete
            multiple
            options={options}
            disableCloseOnSelect
            filterOptions={(opts) => opts}
            getOptionLabel={(opt) => `${opt.name} (${opt.slug}) `}
            getOptionSelected={(option, value) => option.slug === value.slug}
            loading={loading}
            inputValue={inputValue}
            onInputChange={(_, newVal, reason) => {
              if (reason === 'input') {
                setInputValue(newVal)
              }
              if (reason === 'clear') {
                setInputValue('')
              }
            }}
            value={selectUser}
            onChange={(_, newVal, reason) => {
              if (reason === 'remove-option') {
                const removed = selectUser.find((u) => !newVal.includes(u))
                if (removed) {
                  setRemovedSlugs((prev) => new Set(prev).add(removed.slug))
                }
              }
              setSelectUser(newVal)
            }}
            style={{ width: 350 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Selected ${toCamelCaseAndPlural(role as string)}`}
                placeholder='Favorites'
                onBlur={() => setInputValue('')}
              />
            )}
          />
        </ShareContainer>
      </DialogContent>

      <DialogActions>
        <Button variant='contained' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='outlined' onClick={handleShare}>
          {commitLoading ? <LoadingDots /> : 'Save'}
        </Button>
      </DialogActions>
    </>
  )
}

const ShareContactSkeleton: React.FC<Props> = () => (
  <DialogContent>
    <List>
      <ListItem>
        <Skeleton variant='rect' height='20px' width='100%' />
      </ListItem>
      <ListItem>
        <Skeleton variant='rect' height='20px' width='100%' />
      </ListItem>
    </List>
  </DialogContent>
)

const query = graphql`
  query ShareContactQuery($contactSlug: String!, $searchQuery: String, $role: String) {
    sharedContacts(searchQuery: $searchQuery, role: $role, contactSlug: $contactSlug) {
      slug
      name
      shared(contactSlug: $contactSlug)
    }
  }
`

const mutation = graphql`
  mutation ShareContactMutation($input: infoShareContactsInput!) {
    infoShareContacts(input: $input) {
      clientMutationId
    }
  }
`

export default ShareContact
